import React from 'react';

import isArray from 'lodash/isArray';

import { MIN_CAPTURE_AMOUNT } from './constants';
import PreAuthLineRow from './PreAuthLineRow';
import { PreAuthWithSale } from './types';

type OldestPreauthProps = {
  preauth: PreAuthWithSale;
  captureAmounts: Record<string, number>;
  selectedLines: Record<string, boolean>;
  onCaptureAmountChange: (lineId: string, amount: number) => void;
  onLineSelectionChange: (lineId: string, isSelected: boolean) => void;
  onValidationChange: (lineId: string, isValid: boolean) => void;
  totalCapture: number;
};

function getSalesLinesBody(
  saleLines: SaleLine[],
  captureAmounts: Record<string, number>,
  selectedLines: Record<string, boolean>,
  isTotalBelowMinimum: boolean,
  handleCheckboxChange: (lineId: string, checked: boolean) => void,
  handleAmountChange: (lineId: string, amount: number) => void,
  handleValidationChange: (lineId: string, isValid: boolean) => void
) {
  if (!saleLines) {
    return (
      <tr>
        <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
          Loading transaction lines...
        </td>
      </tr>
    );
  }
  if (saleLines.length === 0) {
    return (
      <tr>
        <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
          No transaction lines found
        </td>
      </tr>
    );
  }
  return saleLines.map((line) => (
    <PreAuthLineRow
      key={line.saleLineID}
      line={line}
      captureAmount={captureAmounts[line.saleLineID] || 0}
      isSelected={selectedLines[line.saleLineID] || false}
      isTotalBelowMinimum={isTotalBelowMinimum}
      onCheckboxChange={handleCheckboxChange}
      onAmountChange={handleAmountChange}
      onValidationChange={handleValidationChange}
    />
  ));
}

const OldestPreauth: React.FC<OldestPreauthProps> = ({
  preauth,
  captureAmounts,
  selectedLines,
  onCaptureAmountChange,
  onLineSelectionChange,
  onValidationChange,
  totalCapture,
}) => {
  const saleLine = preauth.sale?.SaleLines?.SaleLine || [];
  const saleLines: SaleLine[] = isArray(saleLine) ? saleLine : [saleLine];
  const selectAllRef = React.useRef<HTMLInputElement>(null);

  // Calculate select all checkbox state based on selectedLines, not amounts
  const selectedCount = saleLines.filter((line) => selectedLines[line.saleLineID]).length;
  const allSelected = selectedCount === saleLines.length && saleLines.length > 0;
  const someSelected = selectedCount > 0 && selectedCount < saleLines.length;

  // Update indeterminate state
  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const handleCheckboxChange = (lineId: string, checked: boolean) => {
    const line = saleLines.find((l) => l.saleLineID === lineId);
    if (!line) return;

    // Update selection state
    onLineSelectionChange(lineId, checked);
    // Set amount to full total when checking, 0 when unchecking
    // Parse the value to ensure proper decimal precision
    const amount = checked ? parseFloat(parseFloat(line.calcTotal).toFixed(2)) : 0;
    onCaptureAmountChange(lineId, amount);
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;

    // Update all line states
    saleLines.forEach((line) => {
      onLineSelectionChange(line.saleLineID, checked);
      // Parse the value to ensure proper decimal precision
      const amount = checked ? parseFloat(parseFloat(line.calcTotal).toFixed(2)) : 0;
      onCaptureAmountChange(line.saleLineID, amount);
      // Clear validation errors when unchecking
      if (!checked) {
        onValidationChange(line.saleLineID, true);
      }
    });
  };

  const handleAmountChange = (lineId: string, amount: number) => {
    onCaptureAmountChange(lineId, amount);
  };

  return (
    <div className="preauth-details">
      <table className="register-listing">
        <thead>
          <tr>
            <th style={{ width: '30px', textAlign: 'center' }}>
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAllChange}
                title="Select all items to capture full amounts"
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
            </th>
            <th>DESCRIPTION</th>
            <th className="th-money">PRICE</th>
            <th style={{ width: '60px' }}>QTY</th>
            <th className="th-money" style={{ width: '80px' }}>
              DISCOUNT
            </th>
            <th style={{ width: '60px' }}>TAX</th>
            <th className="th-money" style={{ width: '120px' }}>
              AUTHORIZED
            </th>
            <th className="th-money" style={{ width: '120px' }}>
              To Capture
            </th>
          </tr>
        </thead>
        <tbody>
          {getSalesLinesBody(
            saleLines,
            captureAmounts,
            selectedLines,
            totalCapture > 0 && totalCapture <= MIN_CAPTURE_AMOUNT,
            handleCheckboxChange,
            handleAmountChange,
            onValidationChange
          )}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '1px solid #ccc' }}>
            <td
              colSpan={7}
              style={{
                textAlign: 'right',
                fontWeight: 'bold',
                padding: '15px 5px 15px 10px',
                fontSize: '16px',
              }}
            >
              Total capture:
            </td>
            <td
              className="money"
              style={{ fontWeight: 'bold', padding: '15px 0 15px 5px', fontSize: '16px' }}
            >
              <span id="preauth_total_capture">${totalCapture.toFixed(2)}</span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default OldestPreauth;
